// @flow

import React, { type Node, useCallback, useEffect, useState } from 'react';
import { getSafeBoletoBarcode, usePendingIncomingPayment, useIncomingPayment, redirectIfCheckForConfirmationIsEnabled } from '@tectonics/prs/incoming-payment';
import styled, { css } from 'styled-components';
import { Button } from '@tectonics/look-and-feel/volcanes/atoms';
import Clipboard from 'react-clipboard.js';
import { formatBoletoNumber } from '@tectonics/prs/boleto';

export function BoletoNumber(): Node {
  const incomingPayment = usePendingIncomingPayment();
  return (
    <BarcodeInfoWrapper>
      <NumberWrapper>
        {formatBoletoNumber(getSafeBoletoBarcode(incomingPayment))}
      </NumberWrapper>
      <ButtonCopyToClipboard text={getSafeBoletoBarcode(incomingPayment)} />
    </BarcodeInfoWrapper>
  );
}

const BarcodeInfoWrapper = styled.div`
  margin-bottom: 20px;
  display: block;
  padding: 16px;
  justify-content: space-between;
  background-color: ${props => props.theme.colors.info(96)};

  @media (min-width: 840px) {
    display: flex;
    padding: 0;
    margin: 0;
    background-color: transparent;
  }
`;

const NumberWrapper = styled.div`
  font-size: 16px;
  font-family: ${props => props.theme.fontFamily.display};
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  letter-spacing: 1px;
  @media (min-width: 840px) {
    font-size: 13px;
    margin-bottom: 0;
    letter-spacing: 0;
  }
`;

function ButtonCopyToClipboard({ text }: { text: string }): Node {
  const [copied, setCopied] = useState(false);
  const [timeoutId, setTimeoutId] = useState();
  const incomingPayment = useIncomingPayment();

  useEffect(() => {
    return () => clearTimeout(timeoutId);
  }, [timeoutId]);

  const handleClick = useCallback(() => {
    setCopied(true);
    setTimeoutId(setTimeout(() => {
      setCopied(false);
      redirectIfCheckForConfirmationIsEnabled(incomingPayment);
    }, 1500));
  }, [setCopied, incomingPayment]);
  let buttonTitle = copied ? 'Copiado' : 'Copiar';

  return (
    <Button as={Clipboard} title={buttonTitle} aria-label={buttonTitle} type="info" outline={!copied} flat onClick={handleClick} data-clipboard-text={text} data-test-id={'button_boleto_copy'}>
      <ButtonCopyToClipboardMessage copied={copied} />
    </Button>
  );
}

const ButtonCopyToClipboardMessage = styled.span`
  position: relative;

  ::before {
    content: 'Copiar';
    opacity: 1;
    position: absolute;
    left: 0;
    right: 0;

    ${props => props.copied && css`
        opacity: 0;
      `}
  }
  ::after {
    content: 'Copiado!';
    opacity: 0;

    ${props => props.copied && css`
        opacity: 1;
      `}
  }
`;
